import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useLatest } from '../../hooks/useLatest';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FiPlus, FiSearch, FiEye, FiEdit, FiTrash2, FiDownload, FiX, FiChevronDown, FiChevronUp, FiLink, FiCopy, FiInfo } from 'react-icons/fi';
import { studentsAPI, usersAPI, settingsAPI, jobOpeningsAPI } from '../../services/api';
import StatusBadge from '../../components/Common/StatusBadge';
import ColumnCustomizer, { useColumnPreferences } from '../../components/Common/ColumnCustomizer';
import { UnifiedFilterHeaderCell } from '../../components/Common/UnifiedTableFilters';
import {
  runTableFilters,
  hasActiveClientTableFilters,
  CLIENT_SIDE_TABLE_FILTER_FETCH_LIMIT
} from '../../utils/tableFilters';
import { useAuth } from '../../context/AuthContext';
import { useCountry } from '../../context/CountryContext';
import Modal from '../../components/Common/Modal';
import { toast } from 'react-toastify';

const StudentList = () => {
  const { t } = useTranslation();
  const { canPerformAction, hasModuleAccess, user } = useAuth();
  const { activeCountry } = useCountry();
  const isFranceCRM = String(activeCountry || '').toUpperCase() === 'FRANCE';
  const statusColumnLabel = isFranceCRM ? t('students.tableAdmissionStatus') : t('students.tableStatus');
  const canViewCompanyStatus = isFranceCRM && hasModuleAccess('student_company_status');
  const canEditCompanyStatus = canPerformAction('student_company_status', 'update');

  const studentColumns = useMemo(() => {
    const cols = [
      { key: 'student_ref', label: t('students.tableId') },
      { key: 'name', label: t('students.tableName') },
      { key: 'email', label: t('students.tableEmail') },
      { key: 'phone', label: t('students.tablePhone') },
      { key: 'agency_name', label: t('students.tableAgency') },
      { key: 'student_type', label: t('students.tableType') },
      { key: 'formation_type', label: t('studentForm.formationType'), defaultVisible: isFranceCRM },
      { key: 'status', label: statusColumnLabel },
      ...(canViewCompanyStatus ? [{ key: 'company_status', label: t('students.tableCompanyStatus') }] : []),
      { key: 'owner_name', label: t('students.tableOwner') },
      { key: 'campus_name', label: t('common.campus'), defaultVisible: false },
      { key: 'intake_name', label: t('common.intake'), defaultVisible: false },
      { key: 'closed_by_name', label: t('leadDetail.closedBy'), forceVisibleOnMigration: true },
      { key: 'lead_ref', label: t('students.leadRef', 'Lead Ref'), defaultVisible: false },
      { key: 'dob', label: t('studentForm.dob', 'Date of Birth'), defaultVisible: false },
      { key: 'gender', label: t('common.gender'), defaultVisible: false },
      { key: 'passport_no', label: t('studentForm.passportNo', 'Passport No'), defaultVisible: false },
      { key: 'country', label: t('common.country'), defaultVisible: false },
      { key: 'city', label: t('common.city'), defaultVisible: false },
      { key: 'current_degree', label: t('studentForm.currentDegree', 'Current Degree'), defaultVisible: false },
      { key: 'language', label: t('studentForm.language', 'Language'), defaultVisible: false },
      { key: 'source', label: t('common.source', 'Source'), defaultVisible: false },
      { key: 'enrollment_number', label: t('studentForm.enrollmentNumber', 'Enrollment No'), defaultVisible: false },
      { key: 'created_at', label: t('common.createdAt', 'Created'), defaultVisible: false },
      { key: 'actions', label: t('students.tableActions'), locked: true }
    ];
    return cols;
  }, [t, canViewCompanyStatus, statusColumnLabel]);
  const { isVisible, toggleColumn, resetToDefault, columns: stuColDefs } = useColumnPreferences('students', studentColumns);

  const [students, setStudents] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(25);
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({
    status: '',
    student_type: '',
    owner_id: '',
    campus_id: '',
    intake_id: '',
    passport_no: '',
    phone: '',
    email: ''
  });
  const [jobApplyFilter, setJobApplyFilter] = useState('all'); // all | applied | not_applied
  const [salesTeam, setSalesTeam] = useState([]);
  const [studentStatuses, setStudentStatuses] = useState([]);
  const [companyStatusOptions, setCompanyStatusOptions] = useState([]);
  const [campuses, setCampuses] = useState([]);
  const [intakes, setIntakes] = useState([]);
  const [tiedUpAgencies, setTiedUpAgencies] = useState([]);
  const [showIntakeLinkModal, setShowIntakeLinkModal] = useState(false);
  const [intakeLinks, setIntakeLinks] = useState([]);
  const [intakeGenerating, setIntakeGenerating] = useState(false);
  const [intakeForm, setIntakeForm] = useState({ student_type: 'b2c', agency_id: '', form_language: 'en' });
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [updatingStatusId, setUpdatingStatusId] = useState(null);
  const [updatingCompanyStatusId, setUpdatingCompanyStatusId] = useState(null);
  const [tableFilters, setTableFilters] = useState({ globalSearch: '', columns: {} });
  const navigate = useNavigate();
  const isSuperAdminOnly = String(user?.role || '').toLowerCase() === 'super_admin';
  const roleLower = String(user?.role || '').toLowerCase();
  const isSalesExecutive = roleLower === 'sales_executive';

  const [openInfoStudentId, setOpenInfoStudentId] = useState(null);
  const [jobInfoByStudentId, setJobInfoByStudentId] = useState({});
  const infoRef = useRef(null);

  useEffect(() => {
    const passport = searchParams.get('passport_no');
    const phone = searchParams.get('phone');
    const email = searchParams.get('email');
    const studentType = searchParams.get('student_type');
    const statusParam = searchParams.get('status');
    const hasExact = passport || phone || email;
    const typeOk = studentType === 'b2b' || studentType === 'b2c';
    const statusOk = statusParam != null && String(statusParam).trim() !== '';
    if (!hasExact && !typeOk && !statusOk) return;
    setFilters((f) => ({
      ...f,
      ...(typeOk ? { student_type: studentType } : {}),
      ...(statusOk ? { status: String(statusParam).trim() } : {}),
      ...(passport != null ? { passport_no: passport } : {}),
      ...(phone != null ? { phone: phone } : {}),
      ...(email != null ? { email: email } : {})
    }));
    setPage(1);
    if (hasExact) setAdvancedOpen(true);
  }, [searchParams]);

  // B2B intake link agency list is scoped by CRM country (`X-Country`); refresh when switching India/France so IDs stay valid.
  useEffect(() => {
    setIntakeForm((f) => ({ ...f, agency_id: '' }));
  }, [activeCountry]);

  useEffect(() => {
    const france = String(activeCountry || '').toUpperCase() === 'FRANCE';
    Promise.all([
      usersAPI.getSalesTeam().catch(() => ({ data: [] })),
      settingsAPI.getStudentStatuses().catch(() => ({ data: [] })),
      settingsAPI.getCampuses().catch(() => ({ data: [] })),
      settingsAPI.getIntakes().catch(() => ({ data: [] })),
      settingsAPI.getTiedUpAgencies().catch(() => ({ data: [] })),
      france ? settingsAPI.getStudentCompanyStatuses().catch(() => ({ data: [] })) : Promise.resolve({ data: [] })
    ]).then(([usersRes, statusesRes, campusesRes, intakesRes, tiedUpRes, companyStatusesRes]) => {
      setSalesTeam(usersRes.data || []);
      setStudentStatuses((statusesRes.data || []).filter(s => s.status === 'active').map(s => s.status_name));
      setCampuses((campusesRes.data || []).filter((c) => c.status === 'active'));
      setIntakes((intakesRes.data || []).filter((i) => i.status === 'active'));
      setTiedUpAgencies(tiedUpRes.data || []);
      setCompanyStatusOptions(
        france
          ? (companyStatusesRes.data || []).filter((s) => s.status === 'active').map((s) => s.status_name)
          : []
      );
    });
  }, [activeCountry]);

  useEffect(() => {
    // France-only feature: hide/disable when not in France CRM
    if (!isFranceCRM && jobApplyFilter !== 'all') setJobApplyFilter('all');
    if (!isFranceCRM && openInfoStudentId) setOpenInfoStudentId(null);
  }, [isFranceCRM, jobApplyFilter, openInfoStudentId]);

  useEffect(() => {
    const onDoc = (e) => {
      if (!openInfoStudentId) return;
      if (infoRef.current && !infoRef.current.contains(e.target)) {
        setOpenInfoStudentId(null);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [openInfoStudentId]);

  const openStudentJobInfo = async (studentId) => {
    setOpenInfoStudentId((prev) => (prev === studentId ? null : studentId));
    if (jobInfoByStudentId[studentId]) return;
    try {
      const res = await jobOpeningsAPI.getByStudent(studentId);
      setJobInfoByStudentId((prev) => ({ ...prev, [studentId]: res.data?.jobs || [] }));
    } catch (_) {
      setJobInfoByStudentId((prev) => ({ ...prev, [studentId]: [] }));
    }
  };

  const loadIntakeLinks = useCallback(async () => {
    try {
      const { data } = await studentsAPI.getIntakeLinks();
      setIntakeLinks(data || []);
    } catch (_) {
      setIntakeLinks([]);
    }
  }, []);

  const copyToClipboard = async (text, successMessage) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(successMessage || t('students.linkCopied'));
    } catch (_) {
      toast.error(t('students.copyFailed'));
    }
  };

  const handleCreateIntakeLink = async () => {
    if (intakeForm.student_type === 'b2b' && !intakeForm.agency_id) {
      alert('Please select agency for B2B');
      return;
    }
    try {
      setIntakeGenerating(true);
      const { data } = await studentsAPI.createIntakeLink(intakeForm);
      await loadIntakeLinks();
      if (data?.url) copyToClipboard(data.url);
      setIntakeForm({ student_type: 'b2c', agency_id: '', form_language: 'en' });
    } catch (err) {
      alert(err.response?.data?.message || 'Could not generate link');
    } finally {
      setIntakeGenerating(false);
    }
  };

  const handleDeactivateIntakeLink = async (id) => {
    if (!window.confirm(t('common.confirm'))) return;
    try {
      await studentsAPI.deactivateIntakeLink(id);
      await loadIntakeLinks();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to deactivate link');
    }
  };

  const clearAllFilters = () => {
    setSearch('');
    setFilters({
      status: '',
      student_type: '',
      owner_id: '',
      campus_id: '',
      intake_id: '',
      passport_no: '',
      phone: '',
      email: ''
    });
    setPage(1);
    setAdvancedOpen(false);
  };

  const exactFilterCount = [filters.passport_no, filters.phone, filters.email].filter((v) => v && String(v).trim()).length;

  const sel = { fontSize: 13, padding: '6px 10px', minHeight: 34, borderRadius: 8, border: '1px solid var(--border)', background: 'var(--white)', color: 'var(--text)', maxWidth: '100%' };
  const inp = { fontSize: 13, padding: '6px 10px', height: 34, borderRadius: 8, border: '1px solid var(--border)', background: 'var(--white)', color: 'var(--text)', width: '100%', boxSizing: 'border-box' };

  const handleDelete = async (id) => {
    if (!window.confirm(t('common.confirm'))) return;
    try {
      await studentsAPI.delete(id);
      loadStudents();
    } catch (err) {
      alert(err.response?.data?.message || t('students.deleteFailed'));
    }
  };

  const handleStatusUpdate = async (studentId, nextStatus) => {
    if (!nextStatus) return;
    try {
      setUpdatingStatusId(studentId);
      await studentsAPI.update(studentId, { status: nextStatus });
      setStudents(prev => prev.map(s => s.student_id === studentId ? { ...s, status: nextStatus } : s));
    } catch (err) {
      alert(err.response?.data?.message || t('students.statusUpdateFailed'));
    } finally {
      setUpdatingStatusId(null);
    }
  };

  const handleCompanyStatusUpdate = async (studentId, nextCompanyStatus) => {
    try {
      setUpdatingCompanyStatusId(studentId);
      const value = nextCompanyStatus ? String(nextCompanyStatus) : null;
      await studentsAPI.updateCompanyStatus(studentId, { company_status: value });
      setStudents((prev) => prev.map((s) => (
        s.student_id === studentId ? { ...s, company_status: value } : s
      )));
    } catch (err) {
      alert(err.response?.data?.message || t('students.companyStatusUpdateFailed'));
    } finally {
      setUpdatingCompanyStatusId(null);
    }
  };

  const handleExport = () => {
    const headers = ['Student ID', 'Name', 'Email', 'Phone', 'Status', 'Owner', 'Agency', 'Created'];
    const rows = students.map(s => [s.student_ref, `${s.first_name} ${s.last_name}`, s.email, s.phone, s.status, s.owner_name || '', s.agency_name || '', s.created_at]);
    const csv = [headers.join(','), ...rows.map(r => r.map(v => `"${v || ''}"`).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'students_export.csv'; a.click();
  };

  const statusOptions = studentStatuses;
  const studentColumnFilterDefs = useMemo(() => {
    const ownerOpts = Array.from(new Set((students || []).map((s) => String(s.owner_name || '')).filter(Boolean)));
    const campusOpts = Array.from(new Set((students || []).map((s) => String(s.campus_name || '')).filter(Boolean)));
    return [
      { key: 'student_ref', label: t('students.tableId'), field: 'student_ref', type: 'text' },
      { key: 'name', label: t('students.tableName'), field: 'student_full_name', type: 'text' },
      { key: 'email', label: t('students.tableEmail'), field: 'email', type: 'text' },
      { key: 'phone', label: t('students.tablePhone'), field: 'phone', type: 'text' },
      { key: 'agency_name', label: t('students.tableAgency'), field: 'agency_name', type: 'text' },
      { key: 'student_type', label: t('students.tableType'), field: 'student_type', type: 'multi', options: [{ value: 'b2c', label: t('common.b2c', 'B2C') }, { value: 'b2b', label: t('common.b2b', 'B2B') }] },
      { key: 'formation_type', label: t('studentForm.formationType'), field: 'formation_type', type: 'text' },
      { key: 'status', label: statusColumnLabel, field: 'status', type: 'multi', options: statusOptions.map((v) => ({ value: v, label: v })) },
      ...(canViewCompanyStatus ? [{
        key: 'company_status',
        label: t('students.tableCompanyStatus'),
        field: 'company_status',
        type: 'multi',
        options: companyStatusOptions.map((v) => ({ value: v, label: v }))
      }] : []),
      { key: 'owner_name', label: t('students.tableOwner'), field: 'owner_name', type: 'multi', options: ownerOpts.map((v) => ({ value: v, label: v })) },
      { key: 'campus_name', label: t('common.campus'), field: 'campus_name', type: 'multi', options: campusOpts.map((v) => ({ value: v, label: v })) },
      { key: 'created_at', label: t('common.createdAt', 'Created'), field: 'created_at', type: 'date' }
    ];
  }, [students, statusOptions, companyStatusOptions, canViewCompanyStatus, t, statusColumnLabel]);
  const studentColumnFilterDefsRef = useLatest(studentColumnFilterDefs);
  const studentColumnMap = useMemo(() => Object.fromEntries(studentColumnFilterDefs.map((d) => [d.key, d])), [studentColumnFilterDefs]);
  const updateColumnFilters = useCallback((updater) => setTableFilters((prev) => ({ ...prev, columns: updater(prev.columns || {}) })), []);
  const normalizedStudents = useMemo(
    () => (students || []).map((s) => ({ ...s, student_full_name: `${s.title || ''} ${s.first_name || ''} ${s.last_name || ''}`.trim() })),
    [students]
  );
  const filteredStudents = useMemo(
    () => runTableFilters(normalizedStudents, tableFilters, tableFilters, [], studentColumnFilterDefs),
    [normalizedStudents, tableFilters, studentColumnFilterDefs]
  );
  const clientTableFilterMode = useMemo(
    () => hasActiveClientTableFilters(tableFilters, studentColumnFilterDefs),
    [tableFilters, studentColumnFilterDefs]
  );
  const pagedFilteredStudents = useMemo(() => {
    if (!clientTableFilterMode) return filteredStudents;
    const start = (page - 1) * limit;
    return filteredStudents.slice(start, start + limit);
  }, [clientTableFilterMode, filteredStudents, page, limit]);

  const listTotalForPaging = clientTableFilterMode ? filteredStudents.length : total;

  useEffect(() => {
    const tp = Math.max(1, Math.ceil(listTotalForPaging / limit));
    if (page > tp) setPage(tp);
  }, [clientTableFilterMode, filteredStudents.length, total, limit, page]);

  const loadStudents = useCallback(async () => {
    setLoading(true);
    try {
      const active = clientTableFilterMode;
      const res = await studentsAPI.getAll({
        page: active ? 1 : page,
        limit: active ? CLIENT_SIDE_TABLE_FILTER_FETCH_LIMIT : limit,
        search,
        ...(isFranceCRM && jobApplyFilter === 'not_applied' ? { no_job_applications: 1 } : {}),
        ...(isFranceCRM && jobApplyFilter === 'applied' ? { has_job_applications: 1 } : {}),
        ...Object.fromEntries(Object.entries(filters).filter(([, v]) => v))
      });
      setStudents(res.data.students);
      setTotal(res.data.total);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  }, [page, limit, search, filters, jobApplyFilter, isFranceCRM, clientTableFilterMode]);

  useEffect(() => { loadStudents(); }, [loadStudents]);

  const totalPages = Math.max(1, Math.ceil(listTotalForPaging / limit));

  return (
    <div>
      <div className="page-header">
        <h1>{t('students.title')}</h1>
        <div className="page-actions">
          {isSuperAdminOnly && (
            <button className="btn btn-outline btn-sm" onClick={handleExport}><FiDownload /> {t('common.exportCsv')}</button>
          )}
          {canPerformAction('students', 'create') && (
            <button
              className="btn btn-outline"
              onClick={async () => {
                await loadIntakeLinks();
                setShowIntakeLinkModal(true);
              }}
            >
              <FiLink /> {t('students.studentFormLink')}
            </button>
          )}
          {canPerformAction('students', 'create') && <button className="btn btn-primary" onClick={() => navigate('/students/new')}><FiPlus /> {t('students.addStudent')}</button>}
        </div>
      </div>

      <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: '0 0 14px', lineHeight: 1.5, maxWidth: 920 }}>
        {t('students.statusPipelineHint')}
      </p>

      <div className="table-container">
        <div
          style={{
            padding: '10px 12px',
            background: 'var(--bg-secondary, #f8fafc)',
            borderBottom: '1px solid var(--border)'
          }}
        >
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
            <div className="table-search" style={{ flex: '1 1 200px', minWidth: 180, maxWidth: 400, margin: 0 }}>
              <FiSearch className="search-icon" />
              <input
                placeholder={t('students.searchPlaceholder')}
                value={tableFilters.globalSearch}
                onChange={(e) => { setTableFilters((prev) => ({ ...prev, globalSearch: e.target.value })); setPage(1); }}
                style={{ fontSize: 13 }}
              />
            </div>
            <select style={sel} value={filters.status} onChange={(e) => { setFilters((f) => ({ ...f, status: e.target.value })); setPage(1); }} title={t('students.statusFilter')}>
              <option value="">{t('students.statusFilter')}</option>
              {statusOptions.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
            <select style={sel} value={filters.student_type} onChange={(e) => { setFilters((f) => ({ ...f, student_type: e.target.value })); setPage(1); }} title={t('students.typeFilter')}>
              <option value="">{t('students.typeFilter')}</option>
              <option value="b2c">{t('common.b2c')}</option>
              <option value="b2b">{t('common.b2b')}</option>
            </select>
            <select style={{ ...sel, minWidth: 120 }} value={filters.owner_id} onChange={(e) => { setFilters((f) => ({ ...f, owner_id: e.target.value })); setPage(1); }} title={t('students.ownerFilter')}>
              <option value="">{t('students.ownerFilter')}</option>
              {salesTeam.map((u) => (
                <option key={u.user_id} value={u.user_id}>{u.name}</option>
              ))}
            </select>
            <select style={{ ...sel, minWidth: 120 }} value={filters.campus_id} onChange={(e) => { setFilters((f) => ({ ...f, campus_id: e.target.value })); setPage(1); }} title={t('students.campusFilter')}>
              <option value="">{t('students.campusFilter')}</option>
              {campuses.map((c) => (
                <option key={c.campus_id} value={c.campus_id}>{c.campus_name}</option>
              ))}
            </select>
            <select style={{ ...sel, minWidth: 120 }} value={filters.intake_id} onChange={(e) => { setFilters((f) => ({ ...f, intake_id: e.target.value })); setPage(1); }} title={t('students.intakeFilter')}>
              <option value="">{t('students.intakeFilter')}</option>
              {intakes.map((i) => (
                <option key={i.intake_id} value={i.intake_id}>{i.intake_name}</option>
              ))}
            </select>
            <button
              type="button"
              className={`btn btn-sm ${advancedOpen || exactFilterCount > 0 ? 'btn-primary' : 'btn-outline'}`}
              onClick={() => setAdvancedOpen((o) => !o)}
              title={t('students.passportPhoneEmail')}
              style={{ whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 4 }}
            >
              {advancedOpen ? <FiChevronUp size={16} /> : <FiChevronDown size={16} />}
              {t('students.exact')}
              {exactFilterCount > 0 ? <span style={{ opacity: 0.9, fontSize: 11 }}>({exactFilterCount})</span> : null}
            </button>
            <button type="button" className="btn btn-sm btn-outline" onClick={clearAllFilters} title={t('students.resetAll')}>
              <FiX size={16} />
            </button>
            <ColumnCustomizer columns={stuColDefs} isVisible={isVisible} toggleColumn={toggleColumn} resetToDefault={resetToDefault} />
            {isFranceCRM && !isSalesExecutive && (
              <div style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                <button
                  type="button"
                  className={`btn btn-sm ${jobApplyFilter === 'applied' ? 'btn-primary' : 'btn-outline'}`}
                  onClick={() => { setJobApplyFilter((v) => (v === 'applied' ? 'all' : 'applied')); setPage(1); }}
                  title="Show students who applied to a company/job"
                  style={{ whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 6 }}
                >
                  <FiInfo size={15} />
                  Applied
                </button>
                <button
                  type="button"
                  className={`btn btn-sm ${jobApplyFilter === 'not_applied' ? 'btn-primary' : 'btn-outline'}`}
                  onClick={() => { setJobApplyFilter((v) => (v === 'not_applied' ? 'all' : 'not_applied')); setPage(1); }}
                  title="Show students not applied to any company/job"
                  style={{ whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 6 }}
                >
                  <FiInfo size={15} />
                  Not Applied
                </button>
              </div>
            )}
          </div>
          {advancedOpen && (
            <div
              style={{
                marginTop: 10,
                padding: '10px 10px 8px',
                borderRadius: 8,
                border: '1px solid var(--border)',
                background: 'var(--white)',
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
                gap: '8px 12px'
              }}
            >
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>{t('students.passport')}</label>
                <input type="text" placeholder={t('students.passportPlaceholder')} value={filters.passport_no} style={inp} autoComplete="off"
                  onChange={(e) => { setFilters((f) => ({ ...f, passport_no: e.target.value })); setPage(1); }} />
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>{t('common.phone')}</label>
                <input type="text" placeholder={t('students.phonePlaceholder')} value={filters.phone} style={inp} autoComplete="off"
                  onChange={(e) => { setFilters((f) => ({ ...f, phone: e.target.value })); setPage(1); }} />
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>{t('common.email')}</label>
                <input type="text" placeholder={t('students.emailPlaceholder')} value={filters.email} style={inp} autoComplete="off"
                  onChange={(e) => { setFilters((f) => ({ ...f, email: e.target.value })); setPage(1); }} />
              </div>
            </div>
          )}
        </div>

        {loading ? <div className="loading-spinner"><div className="spinner"></div></div> : (
          <>
            <div className="table-responsive">
              <table>
                <thead>
                  <tr>
                    {isVisible('student_ref') && <UnifiedFilterHeaderCell column={studentColumnMap.student_ref} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />}
                    {isVisible('name') && <UnifiedFilterHeaderCell column={studentColumnMap.name} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />}
                    {isVisible('email') && <UnifiedFilterHeaderCell column={studentColumnMap.email} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />}
                    {isVisible('phone') && <UnifiedFilterHeaderCell column={studentColumnMap.phone} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />}
                    {isVisible('agency_name') && <UnifiedFilterHeaderCell column={studentColumnMap.agency_name} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />}
                    {isVisible('student_type') && <UnifiedFilterHeaderCell column={studentColumnMap.student_type} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />}
                    {isVisible('formation_type') && <UnifiedFilterHeaderCell column={studentColumnMap.formation_type} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />}
                    {isVisible('status') && <UnifiedFilterHeaderCell column={studentColumnMap.status} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />}
                    {isVisible('company_status') && <UnifiedFilterHeaderCell column={studentColumnMap.company_status} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />}
                    {isVisible('owner_name') && <UnifiedFilterHeaderCell column={studentColumnMap.owner_name} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />}
                    {isVisible('campus_name') && <UnifiedFilterHeaderCell column={studentColumnMap.campus_name} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />}
                    {isVisible('intake_name') && <th>{t('common.intake')}</th>}
                    {isVisible('closed_by_name') && <th>{t('leadDetail.closedBy')}</th>}
                    {isVisible('lead_ref') && <th>{t('students.leadRef', 'Lead Ref')}</th>}
                    {isVisible('dob') && <th>{t('studentForm.dob', 'Date of Birth')}</th>}
                    {isVisible('gender') && <th>{t('common.gender')}</th>}
                    {isVisible('passport_no') && <th>{t('studentForm.passportNo', 'Passport No')}</th>}
                    {isVisible('country') && <th>{t('common.country')}</th>}
                    {isVisible('city') && <th>{t('common.city')}</th>}
                    {isVisible('current_degree') && <th>{t('studentForm.currentDegree', 'Current Degree')}</th>}
                    {isVisible('language') && <th>{t('studentForm.language', 'Language')}</th>}
                    {isVisible('source') && <th>{t('common.source', 'Source')}</th>}
                    {isVisible('enrollment_number') && <th>{t('studentForm.enrollmentNumber', 'Enrollment No')}</th>}
                    {isVisible('created_at') && <UnifiedFilterHeaderCell column={studentColumnMap.created_at} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />}
                    <th>{t('students.tableActions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedFilteredStudents.map(s => (
                    <tr key={s.student_id}>
                      {isVisible('student_ref') && <td>
                        <button type="button" onClick={() => navigate(`/students/${s.student_id}`)}
                          style={{ background: 'none', border: 'none', padding: 0, margin: 0, color: 'var(--primary)', fontWeight: 700, textDecoration: 'underline', cursor: 'pointer' }}
                          title={t('students.openDetails')}>
                          {s.student_ref}
                        </button>
                      </td>}
                      {isVisible('name') && <td style={{ fontWeight: 500 }}>{s.title} {s.first_name} {s.last_name}</td>}
                      {isVisible('email') && (
                        <td style={{ fontSize: 13 }}>
                          {s.email ? (
                            <button
                              type="button"
                              onClick={() => copyToClipboard(s.email, 'Email copied')}
                              style={{ background: 'none', border: 'none', padding: 0, margin: 0, color: 'var(--text)', fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}
                              title="Copy email"
                            >
                              <span>{s.email}</span>
                              <FiCopy size={13} />
                            </button>
                          ) : '-'}
                        </td>
                      )}
                      {isVisible('phone') && (
                        <td style={{ fontSize: 13 }}>
                          {s.phone ? (
                            <button
                              type="button"
                              onClick={() => copyToClipboard(`${s.phone_country_code || ''} ${s.phone || ''}`.trim(), 'Phone number copied')}
                              style={{ background: 'none', border: 'none', padding: 0, margin: 0, color: 'var(--text)', fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}
                              title="Copy phone number"
                            >
                              <span>{`${s.phone_country_code || ''} ${s.phone || ''}`.trim()}</span>
                              <FiCopy size={13} />
                            </button>
                          ) : '-'}
                        </td>
                      )}
                      {isVisible('agency_name') && <td style={{ fontSize: 13 }}>{s.agency_name || '-'}</td>}
                      {isVisible('student_type') && <td>
                        <span className={`badge ${String(s.student_type || 'b2c') === 'b2b' ? 'badge-converted' : 'badge-b2c'}`}>
                          {String(s.student_type || 'b2c').toUpperCase()}
                        </span>
                      </td>}
                      {isVisible('formation_type') && <td style={{ fontSize: 13, textTransform: 'capitalize' }}>{s.formation_type || '-'}</td>}
                      {isVisible('status') && <td>
                        {canPerformAction('students', 'update') ? (
                          <div style={{ display: 'grid', gap: 4 }}>
                            <select value={s.status || ''} disabled={updatingStatusId === s.student_id}
                              onChange={(e) => handleStatusUpdate(s.student_id, e.target.value)}
                              style={{ minWidth: 185, maxWidth: 220, borderRadius: 8, border: '1px solid var(--border-color)', padding: '6px 8px', background: 'var(--card-bg)', fontSize: 12, fontWeight: 600 }}>
                              <option value="">{t('students.selectStatus')}</option>
                              {statusOptions.map(st => <option key={st} value={st}>{st}</option>)}
                            </select>
                            {updatingStatusId === s.student_id && <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{t('students.updating')}</span>}
                          </div>
                        ) : <StatusBadge status={s.status} />}
                      </td>}
                      {isVisible('company_status') && <td>
                        {canEditCompanyStatus ? (
                          <div style={{ display: 'grid', gap: 4 }}>
                            <select
                              value={s.company_status || ''}
                              disabled={updatingCompanyStatusId === s.student_id}
                              onChange={(e) => handleCompanyStatusUpdate(s.student_id, e.target.value)}
                              style={{ minWidth: 165, maxWidth: 200, borderRadius: 8, border: '1px solid var(--border-color)', padding: '6px 8px', background: 'var(--card-bg)', fontSize: 12, fontWeight: 600 }}
                            >
                              <option value="">{t('students.selectCompanyStatus')}</option>
                              {companyStatusOptions.map((st) => <option key={st} value={st}>{st}</option>)}
                            </select>
                            {updatingCompanyStatusId === s.student_id && (
                              <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{t('students.updating')}</span>
                            )}
                          </div>
                        ) : (
                          s.company_status ? <StatusBadge status={s.company_status} /> : '-'
                        )}
                      </td>}
                      {isVisible('owner_name') && <td style={{ fontSize: 13 }}>{s.owner_name || '-'}</td>}
                      {isVisible('campus_name') && <td style={{ fontSize: 13 }}>{s.campus_name || '-'}</td>}
                      {isVisible('intake_name') && <td style={{ fontSize: 13 }}>{s.intake_name || '-'}</td>}
                      {isVisible('closed_by_name') && <td style={{ fontSize: 13 }}>{s.closed_by_name || '-'}</td>}
                      {isVisible('lead_ref') && <td style={{ fontSize: 13 }}>{s.lead_ref || '-'}</td>}
                      {isVisible('dob') && <td style={{ fontSize: 13 }}>{s.dob ? new Date(s.dob).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' }) : '-'}</td>}
                      {isVisible('gender') && <td style={{ fontSize: 13 }}>{s.gender || '-'}</td>}
                      {isVisible('passport_no') && <td style={{ fontSize: 13 }}>{s.passport_no || '-'}</td>}
                      {isVisible('country') && <td style={{ fontSize: 13 }}>{s.country || '-'}</td>}
                      {isVisible('city') && <td style={{ fontSize: 13 }}>{s.city || '-'}</td>}
                      {isVisible('current_degree') && <td style={{ fontSize: 13 }}>{s.current_degree || '-'}</td>}
                      {isVisible('language') && <td style={{ fontSize: 13 }}>{s.language || '-'}</td>}
                      {isVisible('source') && <td style={{ fontSize: 13 }}>{s.source || '-'}</td>}
                      {isVisible('enrollment_number') && <td style={{ fontSize: 13 }}>{s.enrollment_number || '-'}</td>}
                      {isVisible('created_at') && <td style={{ fontSize: 13 }}>{s.created_at ? new Date(s.created_at).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' }) : '-'}</td>}
                      <td>
                        <div className="table-actions">
                          {isFranceCRM && (
                            <div style={{ position: 'relative', display: 'inline-flex' }}>
                              <button
                                className="btn btn-sm btn-outline"
                                onClick={(e) => { e.stopPropagation(); openStudentJobInfo(s.student_id); }}
                                title="Student job applications"
                                style={{ padding: '4px 8px' }}
                              >
                                <FiInfo />
                              </button>
                              {openInfoStudentId === s.student_id && (
                                <div
                                  ref={infoRef}
                                  style={{
                                    position: 'absolute',
                                    right: 0,
                                    top: '110%',
                                    width: 360,
                                    maxWidth: '70vw',
                                    background: 'var(--card-bg, #fff)',
                                    border: '1px solid var(--border)',
                                    borderRadius: 10,
                                    boxShadow: '0 12px 28px rgba(0,0,0,0.14)',
                                    zIndex: 50,
                                    padding: 12
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                                    <div style={{ fontWeight: 800, fontSize: 13 }}>Applied Jobs</div>
                                    <button
                                      type="button"
                                      onClick={() => setOpenInfoStudentId(null)}
                                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}
                                      title="Close"
                                    >
                                      <FiX />
                                    </button>
                                  </div>

                                  {jobInfoByStudentId[s.student_id] === undefined ? (
                                    <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Loading...</div>
                                  ) : jobInfoByStudentId[s.student_id].length === 0 ? (
                                    <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>No job applications found.</div>
                                  ) : (
                                    <div style={{ display: 'grid', gap: 10, maxHeight: 320, overflow: 'auto', paddingRight: 4 }}>
                                      {jobInfoByStudentId[s.student_id].slice(0, 30).map((j) => (
                                        <div key={j.job_student_link_id || j.job_id} style={{ border: '1px solid var(--border-light, #f1f5f9)', borderRadius: 10, padding: 10 }}>
                                          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                                            <div style={{ minWidth: 0 }}>
                                              <button
                                                type="button"
                                                onClick={() => navigate(`/companies/${j.company_id}`)}
                                                style={{ background: 'none', border: 'none', padding: 0, margin: 0, cursor: 'pointer', color: 'var(--primary)', fontWeight: 700, fontSize: 12, textAlign: 'left' }}
                                                title="Open company"
                                              >
                                                {j.company_name || '—'}
                                              </button>
                                              <div>
                                                <button
                                                  type="button"
                                                  onClick={() => navigate(`/jobs/${j.job_id}`)}
                                                  style={{ background: 'none', border: 'none', padding: 0, margin: 0, cursor: 'pointer', color: 'var(--text)', fontWeight: 600, fontSize: 12, textAlign: 'left' }}
                                                  title="Open job"
                                                >
                                                  {j.offer_title || j.job_title || `Job #${j.job_id}`}
                                                </button>
                                              </div>
                                            </div>
                                            <span style={{
                                              fontSize: 11, fontWeight: 800, padding: '2px 8px', borderRadius: 999,
                                              background: '#e8f0fe', color: '#1a73e8', height: 'fit-content', whiteSpace: 'nowrap'
                                            }}>
                                              {j.job_student_status || j.status || 'Applied'}
                                            </span>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                          <button className="view" onClick={() => navigate(`/students/${s.student_id}`)}><FiEye /></button>
                          {canPerformAction('students', 'update') && <button className="edit" onClick={() => navigate(`/students/${s.student_id}/edit`)}><FiEdit /></button>}
                          {canPerformAction('students', 'delete') && <button className="delete" onClick={() => handleDelete(s.student_id)}><FiTrash2 /></button>}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {filteredStudents.length === 0 && (
                    <tr><td colSpan={100} className="text-center" style={{ padding: 40, color: '#999' }}>{t('students.noStudents')}</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="table-pagination">
              <span>{t('students.showingStudents', { start: listTotalForPaging > 0 ? ((page - 1) * limit) + 1 : 0, end: Math.min(page * limit, listTotalForPaging), total: listTotalForPaging })}</span>
              <div className="pagination-btns">
                <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}>{t('common.prev')}</button>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const p = Math.max(1, Math.min(page - 2, totalPages - 4)) + i;
                  return p <= totalPages ? <button key={p} className={p === page ? 'active' : ''} onClick={() => setPage(p)}>{p}</button> : null;
                })}
                <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>{t('common.next')}</button>
              </div>
            </div>
          </>
        )}
      </div>

      <Modal
        isOpen={showIntakeLinkModal}
        onClose={() => setShowIntakeLinkModal(false)}
        title={t('students.intakeLinksTitle')}
        size="lg"
        footer={(
          <>
            <button className="btn btn-outline" onClick={() => setShowIntakeLinkModal(false)}>Close</button>
            <button className="btn btn-primary" onClick={handleCreateIntakeLink} disabled={intakeGenerating}>
              {intakeGenerating ? t('common.loading') : t('students.generateLink')}
            </button>
          </>
        )}
      >
        <div className="form-grid" style={{ marginBottom: 12 }}>
          <div className="form-group">
            <label>{t('students.studentType')}</label>
            <select
              value={intakeForm.student_type}
              onChange={(e) => setIntakeForm((f) => ({ ...f, student_type: e.target.value, agency_id: '' }))}
            >
              <option value="b2c">{t('common.b2c', 'B2C')}</option>
              <option value="b2b">{t('common.b2b', 'B2B')}</option>
            </select>
          </div>
          {intakeForm.student_type === 'b2b' && (
            <div className="form-group">
              <label>{t('students.agencyRequired')}</label>
              <select
                value={intakeForm.agency_id}
                onChange={(e) => setIntakeForm((f) => ({ ...f, agency_id: e.target.value }))}
              >
                <option value="">{t('students.selectAgency', 'Select agency')}</option>
                {tiedUpAgencies.map((a) => (
                  <option key={a.lead_id} value={a.lead_id}>
                    {a.agency_name || a.ref_number || `#${a.lead_id}`}
                  </option>
                ))}
              </select>
            </div>
          )}
          {isFranceCRM && (
            <div className="form-group">
              <label>{t('students.formLanguage', 'Form Language')}</label>
              <select
                value={intakeForm.form_language || 'en'}
                onChange={(e) => setIntakeForm((f) => ({ ...f, form_language: e.target.value }))}
              >
                <option value="en">{t('common.english', 'English')}</option>
                <option value="fr">{t('common.french', 'French')}</option>
              </select>
            </div>
          )}
        </div>
        <div className="table-responsive">
          <table>
            <thead>
              <tr>
                <th>{t('students.created')}</th>
                <th>{t('students.studentType')}</th>
                <th>{t('students.agency', 'Agency')}</th>
                {isFranceCRM && <th>{t('students.formLanguage', 'Form Language')}</th>}
                <th>{t('common.status', 'Status')}</th>
                <th>{t('students.intakeLinkUsedAtColumn', 'Used at')}</th>
                <th>{t('students.tableLink')}</th>
                <th>{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {intakeLinks.map((l) => (
                <tr key={l.intake_link_id}>
                  <td>{l.created_at ? new Date(l.created_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }) : '-'}</td>
                  <td>{String(l.student_type || '').toUpperCase()}</td>
                  <td>{l.agency_name || '-'}</td>
                  {isFranceCRM && <td>{String(l.form_language || 'en').toLowerCase() === 'fr' ? t('common.french', 'French') : t('common.english', 'English')}</td>}
                  <td>{Number(l.is_active) === 1 ? t('common.active', 'Active') : t('common.inactive', 'Inactive')}</td>
                  <td style={{ fontSize: 13, maxWidth: 280 }}>
                    {l.used_at ? (
                      new Date(l.used_at).toLocaleString('en-IN', { timeZone: isFranceCRM ? 'Europe/Paris' : 'Asia/Kolkata' })
                    ) : Number(l.legacy_intake_student_count) > 0 ? (
                      <span title={t('students.intakeLinkLegacyHint')}>
                        {t('students.intakeLinkLikelyUsedLegacy')}
                      </span>
                    ) : (
                      t('students.intakeLinkNotUsed')
                    )}
                  </td>
                  <td style={{ maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.url || '-'}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      {l.url && (
                        <button className="btn btn-sm btn-outline" onClick={() => copyToClipboard(l.url)} title="Copy link">
                          <FiCopy />
                        </button>
                      )}
                      {Number(l.is_active) === 1 && (
                        <button className="btn btn-sm btn-outline btn-danger" onClick={() => handleDeactivateIntakeLink(l.intake_link_id)} title={t('students.deactivate')}>
                          <FiX />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {intakeLinks.length === 0 && (
                <tr>
                  <td colSpan={isFranceCRM ? 8 : 7} style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 16 }}>{t('students.noLinksGenerated')}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Modal>
    </div>
  );
};

export default StudentList;

